import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Lock, User, ArrowRight, Loader2, Eye, EyeOff } from "lucide-react";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#050505] glow-grid flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2.5 mb-8">
          <div className="h-10 w-10 rounded-xl bg-[#0055FF] flex items-center justify-center shadow-[0_0_18px_rgba(0,85,255,0.5)]">
            <span className="font-display font-bold text-white text-xl">E</span>
          </div>
          <div className="font-display font-bold text-white tracking-tight text-xl">ECO</div>
        </Link>

        <div className="eco-card p-8">
          <h1 className="font-display text-2xl font-semibold text-white text-center">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="text-sm text-slate-400 text-center mt-1.5">
            {mode === "login" ? "Sign in to continue learning" : "Start your business education for free"}
          </p>

          <button onClick={googleLogin} data-testid="google-login-btn" className="btn-secondary w-full mt-6">
            <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/></svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-xs text-slate-500 uppercase tracking-wider">or</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <div className="relative">
                <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                <input data-testid="auth-name-input" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Full name" className="eco-input pl-10" />
              </div>
            )}
            <div className="relative">
              <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
              <input data-testid="auth-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="Email address" className="eco-input pl-10" />
            </div>
            <div className="relative">
              <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
              <input data-testid="auth-password-input" type={showPwd ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Password" className="eco-input pl-10 pr-10" />
              <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPwd(!showPwd)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors">
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>

            {mode === "login" && (
              <div className="text-right">
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs text-[#4d8bff] hover:underline">Forgot password?</Link>
              </div>
            )}

            {error && <p data-testid="auth-error" className="text-sm text-red-400">{error}</p>}

            <button type="submit" data-testid="auth-submit-btn" disabled={loading} className="btn-accent w-full">
              {loading ? <Loader2 size={18} className="animate-spin" /> : <>{mode === "login" ? "Sign in" : "Create account"} <ArrowRight size={16} /></>}
            </button>
          </form>

          <p className="text-sm text-slate-400 text-center mt-5">
            {mode === "login" ? "New to ECO?" : "Already have an account?"}{" "}
            <button data-testid="auth-toggle-btn" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} className="text-[#4d8bff] font-medium hover:underline">
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
